import React from "react";
import {
  getPaymentMethod,
  getVerificationStatus,
  getSessionType,
  getAppointmentStatusLabel,
} from "./utils";

export const PaymentMethodBadge = ({ session, paymentDataBySession }) => {
  const paymentMethod = getPaymentMethod(session, paymentDataBySession);

  if (!paymentMethod || paymentMethod === "-" || paymentMethod.trim() === "") {
    return (
      <span
        style={{
          color: "#6B7280",
          fontWeight: "500",
          padding: "6px 12px",
          fontSize: "12px",
          minWidth: "80px",
          textAlign: "center",
          display: "inline-block",
          lineHeight: "1.2",
        }}>
        -
      </span>
    );
  }

  const paymentMethodConfig = {
    "Insurance": { bg: "#DBEAFE", color: "#193CB8", text: "Insurance" },
    "insurance": { bg: "#DBEAFE", color: "#193CB8", text: "Insurance" },
    "Self-Pay": { bg: "#FEE9C6", color: "#973B00", text: "Self-Pay" },
    "self-pay": { bg: "#FEE9C6", color: "#973B00", text: "Self-Pay" },
    "cash": { bg: "#FEE9C6", color: "#973B00", text: "Self-Pay" },
    "Access Code": { bg: "#EDE9FE", color: "#5D0EC1", text: "Access Code" },
    "access code": { bg: "#EDE9FE", color: "#5D0EC1", text: "Access Code" },
  };

  const method = paymentMethod.trim();
  const config = paymentMethodConfig[method] || paymentMethodConfig["Insurance"];

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: "600",
        padding: "6px 14px",
        fontSize: "12px",
        minWidth: "fit-content",
        textAlign: "center",
        borderRadius: "50px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        flexShrink: 0,
        boxSizing: "border-box",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        lineHeight: "1.2",
      }}>
      {config.text}
    </span>
  );
};

export const StatusBadge = ({ session, paymentDataBySession, showBillingDetails = false }) => {
  const verificationStatus = getVerificationStatus(session, paymentDataBySession, showBillingDetails);
  const paymentMethod = getPaymentMethod(session, paymentDataBySession)?.toLowerCase() || "";
  let verifiedTooltip = "Payment and eligibility verified successfully.";
  let pendingTooltip = "Verification incomplete — action may be required.";
  let invalidTooltip = "Invalid or failed verification — appointment requires review.";

  if (paymentMethod === "self-pay" || paymentMethod === "cash") {
    verifiedTooltip = "Payment method is valid/on file.";
    pendingTooltip = "Payment method still needs confirmation.";
    invalidTooltip = "Card/payment method failed or cannot be used.";
  } else if (paymentMethod === "access code") {
    verifiedTooltip = "Access code is valid.";
    pendingTooltip = "Access code still needs review.";
    invalidTooltip = "Access code is expired, revoked, or invalid.";
  } else if (paymentMethod === "insurance") {
    verifiedTooltip = "Insurance has been verified for the session.";
    pendingTooltip = "Insurance verification is not complete.";
    invalidTooltip = "Insurance could not be verified or cannot be used for this session.";
  }

  if (showBillingDetails && !["Verified", "Invalid"].includes(verificationStatus)) {
    return null;
  }

  const statusConfig = {
    Verified: {
      bg: "#d0fae5",
      color: "#006145",
      text: showBillingDetails ? "Paid" : "Verified",
      tooltip: verifiedTooltip,
    },
    Pending: {
      bg: "#FFFED8",
      color: "#687000",
      text: "Pending",
      tooltip: pendingTooltip,
    },
    Invalid: {
      bg: "#FEE4E6",
      color: "#ED3A3A",
      text: showBillingDetails ? "Failed" : "Invalid",
      tooltip: invalidTooltip,
    },
  };

  const config = statusConfig[verificationStatus] || statusConfig["Pending"];

  return (
    <span
      title={config.tooltip}
      style={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: "600",
        padding: "6px 14px",
        fontSize: "12px",
        borderRadius: "15px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        flexShrink: 0,
        boxSizing: "border-box",
        minWidth: "fit-content",
        textAlign: "center",
        cursor: "help",
        lineHeight: "1.2",
      }}>
      {config.text}
    </span>
  );
};

export const SessionTypeCell = ({ session }) => {
  const sessionType = getSessionType(session);

  return (
    <span
      style={{
        color: "#575757",
        fontWeight: "500",
        fontSize: "13px",
        lineHeight: "1.2",
      }}>
      {sessionType}
    </span>
  );
};

export const AppointmentStatusBadge = ({ session }) => {
  const displayStatus = getAppointmentStatusLabel(session);

  const statusConfig = {
    Scheduled: {
      bg: "#DBEAFE",
      color: "#193CB8",
      text: "Scheduled",
    },
    Completed: {
      bg: "#D0FAE5",
      color: "#006145",
      text: "Completed",
    },
    "User Cancel": {
      bg: "#FEF9C2",
      color: "#894B00",
      text: "User Cancel",
    },
    "Provider Cancel": {
      bg: "#EDE9FE",
      color: "#5D0EC1",
      text: "Provider Cancel",
    },
    "Admin Cancel": {
      bg: "#000000",
      color: "#FFFFFF",
      text: "Admin Cancel",
    },
    "Late Cancel": {
      bg: "#FEE9C6",
      color: "#973B00",
      text: "Late Cancel",
    },
    "No Show": {
      bg: "#FEE4E6",
      color: "#ED3A3A",
      text: "No Show",
    },
    Rescheduled: {
      bg: "#FEF3C7",
      color: "#92400E",
      text: "Rescheduled",
    },
  };

  const config = statusConfig[displayStatus] || {
    bg: "#F3F4F6",
    color: "#6B7280",
    text: displayStatus,
  };

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: "600",
        padding: "6px 14px",
        fontSize: "12px",
        borderRadius: "50px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        flexShrink: 0,
        boxSizing: "border-box",
        minWidth: "fit-content",
        textAlign: "center",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        lineHeight: "1.2",
      }}>
      {config.text}
    </span>
  );
};
